import React, { useEffect } from 'react';
import { TrainingDrill } from '../Model';
import { getDifficultyColor, getCategoryColor, getAgeGroupColor } from './drillColors';
import { YouTubeEmbed } from '../YouTubeEmbed';

interface DrillDetailModalProps {
  drill: TrainingDrill;
  onClose: () => void;
}

export const DrillDetailModal: React.FC<DrillDetailModalProps> = ({ drill, onClose }) => {
  const difficultyColors = getDifficultyColor(drill.difficulty);
  const categoryColors = getCategoryColor(drill.category);
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const equipmentItems = Object.entries(drill.equipment)
    .filter(([, qty]) => qty > 0)
    .map(([name, qty]) => `${qty} ${name}`);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="drill-modal-title"
        className="modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header">
          <div className="flex-1">
            <div className="flex gap-2 mb-3 flex-wrap">
              <span
                className="badge"
                style={{
                  backgroundColor: difficultyColors.bg,
                  color: difficultyColors.text,
                  border: `1px solid ${difficultyColors.border}`,
                }}
              >
                {drill.difficulty}
              </span>
              <span
                className="badge"
                style={{
                  backgroundColor: categoryColors.bg,
                  color: categoryColors.text,
                }}
              >
                {drill.category}
              </span>
            </div>
            <h2 id="drill-modal-title" className="modal-title">{drill.name}</h2>
          </div>
          <button onClick={onClose} className="btn-close btn-close-lg ml-4" aria-label="Close">
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Description */}
          <div className="mb-6">
            <h4 className="section-title">Description</h4>
            <p className="text-muted text-base line-height-relaxed m-0">
              {drill.description}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="session-overview mb-6">
            <div className="stat-card">
              <div className="stat-icon">&#9201;</div>
              <div className="stat-value">{drill.duration} min</div>
              <div className="stat-label">Duration</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">&#128101;</div>
              <div className="stat-value">{drill.playersRequired}</div>
              <div className="stat-label">Players Required</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">&#128204;</div>
              <div className="stat-value text-sm">{drill.spaceRequired.width}m x {drill.spaceRequired.height}m</div>
              <div className="stat-label">Space Required</div>
            </div>
          </div>

          {/* Skills */}
          <div className="mb-6">
            <h4 className="section-title">Skills Developed</h4>
            <div className="flex flex-wrap gap-2">
              {drill.skills.map((skill) => (
                <span key={skill} className="badge badge-lg badge-accent">
                  {skill}
                </span>
              ))}
            </div>
          </div>

          {/* Age Groups */}
          {drill.ageGroups && drill.ageGroups.length > 0 && (
            <div className="mb-6">
              <h4 className="section-title">Suitable Age Groups</h4>
              <div className="flex flex-wrap gap-2">
                {drill.ageGroups.map((ageGroup) => {
                  const ageGroupColors = getAgeGroupColor(ageGroup);
                  return (
                    <span
                      key={ageGroup}
                      className="badge badge-lg"
                      style={{
                        backgroundColor: ageGroupColors.bg,
                        color: ageGroupColors.text,
                        border: `1px solid ${ageGroupColors.border}`,
                      }}
                    >
                      {ageGroup}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Equipment */}
          <div className="mb-6">
            <h4 className="section-title">Equipment Needed</h4>
            {equipmentItems.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {equipmentItems.map((item) => (
                  <span key={item} className="badge badge-lg badge-light">
                    {item}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-muted text-base m-0">No equipment required</p>
            )}
          </div>

          {/* YouTube Video Embed */}
          {drill.youtubeUrl && (
            <div className="mb-6">
              <h4 className="section-title">Video Tutorial</h4>
              <YouTubeEmbed url={drill.youtubeUrl} title={drill.name} />
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
